import { type ReactElement } from 'react';
import styles from './my-matrix-remake.module.scss';
import { DashboardMemberView } from '@utmb/types/index';
import { Font } from '@utmb/design-system/text/font';
import { getColorStyle, getLoyaltyColor } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { LoyaltyLevelEnum } from '@utmb/types/enum/LoyaltyLevelEnum';
import { LoyaltyLevelName } from '@utmb/types/view/LoyaltyLevelEnum';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconEnum } from '@utmb/types/enum/IconEnum';
import { PointsSummary } from '../my-utmb/points-summary/points-summary';

export interface MyMatrixRemakeProps {
    dashboardMember: DashboardMemberView;
}

export function MyMatrixRemake({ dashboardMember }: MyMatrixRemakeProps): ReactElement {
    const loyaltyLevels = [LoyaltyLevelEnum.LEVEL1, LoyaltyLevelEnum.LEVEL2, LoyaltyLevelEnum.LEVEL3];

    return (
        <div className={styles.wrapMyMatrixRemake}>
            {dashboardMember.loyalty ? (
                <div className={styles.wrapPointSummary}>
                    <PointsSummary value={dashboardMember.loyalty.points} />
                </div>
            ) : null}

            <div className={styles.myMatrixRemake}>
                <div className={styles.wrapColumn}>
                    <div className={styles.header} style={{ backgroundColor: getColorStyle(ColorTypesEnum.PRIMARY) }}></div>
                    <div className={styles.column}>
                        {dashboardMember.loyalty?.rewardsMatrix?.map((entry) => {
                            const labelMatch = entry.label.match(/^(.*?)\s*\((.*?)\)$/);
                            const hasParentheses = Boolean(labelMatch);
                            const mainLabel = hasParentheses ? labelMatch?.[1].trim() : entry.label;
                            const parenthesesText = hasParentheses ? labelMatch?.[2] : null;

                            return (
                                <div key={entry.label} className={styles.cell}>
                                    <Font.FuturaMedium className={styles.label} desktop="14" mobile="12" color={getColorStyle(ColorTypesEnum.WHITE)}>
                                        {mainLabel}
                                        <Font.FuturaMedium className={styles.label} desktop="10" mobile="10" color={getColorStyle(ColorTypesEnum.WHITE)}>
                                            {parenthesesText && <span className={styles.parenthesesText}> ({parenthesesText})</span>}
                                        </Font.FuturaMedium>
                                    </Font.FuturaMedium>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {loyaltyLevels.map((level) => (
                    <div key={level} className={styles.wrapColumn}>
                        <div className={styles.header} style={{ backgroundColor: getLoyaltyColor(level) }}>
                            <div className={styles.wrapTitle}>
                                <Font.FuturaBold desktop="18" mobile="16">
                                    {LoyaltyLevelName[level]}
                                </Font.FuturaBold>
                                <div className={styles.wrapLinePlus}>
                                    <Icon variant={IconEnum.LINEPLUS} width={100} color={getColorStyle(ColorTypesEnum.WHITE)} />
                                </div>
                            </div>
                            <Font.FuturaMedium className={styles.points} desktop="16" mobile="14">
                                {`+${dashboardMember.loyalty?.levels.find((target) => target.level === level)?.minPoints}pts`}
                            </Font.FuturaMedium>
                            <div className={styles.wrapFourPlus}>
                                <Icon variant={IconEnum.FOURPLUS} width={33} height={33} color={getColorStyle(ColorTypesEnum.WHITE)} />
                            </div>
                        </div>

                        <div className={styles.column}>
                            <div
                                className={styles.shadow}
                                style={{
                                    background: `linear-gradient(45deg, rgba(255, 255, 255, 0) 33%, ${getLoyaltyColor(level)} 100%)`,
                                }}
                            ></div>

                            {dashboardMember.loyalty?.rewardsMatrix?.map((entry) => {
                                const targetValue = entry.values.find((target) => target.level === level);
                                const value = targetValue?.more ?? targetValue?.access ?? false;

                                return (
                                    <div key={entry.label + level} className={styles.cell}>
                                        {value && typeof value === 'string' ? (
                                            (() => {
                                                const match = value.match(/(\d+(?:[.,]\d+)?)(%?)/);
                                                if (!match || match.index === undefined) {
                                                    return (
                                                        <Font.FuturaMedium
                                                            className={styles.label}
                                                            desktop="16"
                                                            mobile="14"
                                                            color={getColorStyle(ColorTypesEnum.WHITE)}
                                                        >
                                                            {value}
                                                        </Font.FuturaMedium>
                                                    );
                                                }

                                                const before = value.slice(0, match.index);
                                                const number = match[1];
                                                const percent = match[2];
                                                const after = value.slice(match.index + number.length + percent.length);
                                                const isPercent = percent === '%';

                                                return (
                                                    <Font.FuturaBook
                                                        className={styles.label}
                                                        desktop="16"
                                                        mobile="14"
                                                        color={getColorStyle(ColorTypesEnum.WHITE)}
                                                        as="span"
                                                    >
                                                        {before}
                                                        {isPercent ? (
                                                            <Font.FuturaBold desktop="20" mobile="18" as="span" style={{ color: getLoyaltyColor(level) }}>
                                                                {number}
                                                                {percent}
                                                            </Font.FuturaBold>
                                                        ) : (
                                                            <Font.FuturaBold desktop="16" mobile="14" as="span">
                                                                {number}
                                                            </Font.FuturaBold>
                                                        )}
                                                        {after}
                                                    </Font.FuturaBook>
                                                );
                                            })()
                                        ) : (
                                            <div
                                                style={{
                                                    background: value ? getLoyaltyColor(level) : getColorStyle(ColorTypesEnum.WHITE),
                                                    boxShadow: value ? `0px 0px 20px 3px ${getLoyaltyColor(level)}` : undefined,
                                                }}
                                                className={`${styles.circle} ${value ? styles.colorCircle : ''}`}
                                            ></div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
